import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { fetchLogs, fetchCustomers } from '../api/admin';

export function LogsPage() {
    const [currentPage, setCurrentPage] = useState(1);
    
    // Filtering States
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [eventTypeFilter, setEventTypeFilter] = useState('all');
    const [customerFilter, setCustomerFilter] = useState('all');
    const [severityFilter, setSeverityFilter] = useState('all');

    // Fetch up to 500 logs for client-side search/filters
    const { data: logsData, isLoading: logsLoading } = useQuery({
        queryKey: ['logs'],
        queryFn: () => fetchLogs({ limit: 500 }),
        placeholderData: keepPreviousData,
    });

    // Fetch customers list to populate the customer selector
    const { data: customersData } = useQuery({
        queryKey: ['customers'],
        queryFn: fetchCustomers,
    });

    const logs = logsData?.logs || [];
    const customers = customersData?.customers || [];

    const formatLogTime = (log: any) => {
        if (log.createdAt) {
            return new Date(log.createdAt).toLocaleString();
        }
        if (log.timestamp) {
            if (typeof log.timestamp === 'object' && log.timestamp._seconds) {
                return new Date(log.timestamp._seconds * 1000).toLocaleString();
            }
            return new Date(log.timestamp).toLocaleString();
        }
        return 'N/A';
    };

    const renderDetails = (log: any) => {
        switch (log.type) {
            case 'ACTIVATE':
            case 'MACHINE_ACTIVATED':
                return `Activated machine "${log.serverName || log.machineName || 'Unknown'}" (IP: ${log.ip || log.ipAddress || 'N/A'}, Version: ${log.version || 'N/A'})`;
            case 'VALIDATE':
                return `Validated license (IP: ${log.ip || log.ipAddress || 'N/A'}, Status: ${log.status || 'VALID'})`;
            case 'HEARTBEAT':
                return `Heartbeat received (IP: ${log.ip || log.ipAddress || 'N/A'})`;
            case 'DEACTIVATE':
            case 'MACHINE_DEACTIVATED':
                return log.details || `Deactivated machine (IP: ${log.ip || log.ipAddress || 'N/A'})`;
            case 'SERVER_REACTIVATED':
            case 'MACHINE_REACTIVATED':
                return `Machine "${log.serverName || log.machineName || 'Unknown'}" reactivated (Count: ${log.reactivationCount || 0})`;
            case 'MACHINE_REACTIVATED_BY_ADMIN':
                return log.details || `Machine "${log.serverName || log.machineName || 'Unknown'}" reactivated by administrator`;
            case 'SERVER_SUSPICIOUS':
            case 'MACHINE_SUSPICIOUS':
                return `Machine "${log.serverName || log.machineName || 'Unknown'}" marked suspicious (Reason: ${log.reason === 'too_many_reactivations' ? 'Too many reactivations' : (log.reason || 'N/A')})`;
            case 'SERVER_BLOCK':
            case 'MACHINE_BLOCK':
                return log.details || `Machine "${log.serverName || log.machineName || 'Unknown'}" blocked`;
            case 'SERVER_UNBLOCK':
            case 'MACHINE_UNBLOCK':
                return log.details || `Machine "${log.serverName || log.machineName || 'Unknown'}" unblocked`;
            case 'SERVER_MIGRATED':
            case 'MACHINE_MIGRATED':
                return log.details || `Machine "${log.serverName || log.machineName || 'Unknown'}" migrated (replaced)`;
            case 'LICENSE_UPDATE':
                const updates = Object.keys(log)
                    .filter(k => !['id', 'logId', 'type', 'timestamp', 'createdAt', 'licenseKey'].includes(k))
                    .map(k => `${k}: ${JSON.stringify(log[k])}`)
                    .join(', ');
                return `Updated properties: ${updates || 'none'}`;
            case 'PRODUCT_CREATE':
                return `Created product "${log.name || 'N/A'}" (${log.productId || 'N/A'}, v${log.version || 'N/A'})`;
            case 'CUSTOMER_CREATE':
                return `Created customer "${log.name || 'N/A'}"`;
            case 'LICENSE_CREATE':
            case 'LICENSE_CREATED':
                return `Generated license key for Product: ${log.productId || 'N/A'}${log.customerId ? ` (Customer: ${log.customerId})` : ''}`;
            case 'SETTINGS_UPDATED':
                return log.details || `System settings updated`;
            case 'SYSTEM_LOG_CLEANUP':
                return log.details || `Executed daily log cleanup`;
            default:
                const detailsObj = { ...log };
                delete detailsObj.id;
                delete detailsObj.logId;
                delete detailsObj.type;
                delete detailsObj.timestamp;
                delete detailsObj.createdAt;
                return JSON.stringify(detailsObj);
        }
    };

    const getBadgeColorClass = (type: string) => {
        switch (type) {
            case 'LICENSE_CREATE':
            case 'LICENSE_CREATED':
            case 'LICENSE_RENEWED':
            case 'MACHINE_UNBLOCK':
            case 'SERVER_UNBLOCK':
            case 'MACHINE_ACTIVATED':
            case 'ACTIVATE':
            case 'VALIDATE':
            case 'HEARTBEAT':
            case 'MACHINE_REACTIVATED_BY_ADMIN':
                return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
            case 'MACHINE_SUSPICIOUS':
            case 'SERVER_SUSPICIOUS':
                return 'bg-yellow-100 text-yellow-850 dark:bg-yellow-950/40 dark:text-yellow-350';
            case 'MACHINE_BLOCK':
            case 'SERVER_BLOCK':
            case 'MACHINE_DEACTIVATED':
            case 'DEACTIVATE':
            case 'DEACTIVATED':
            case 'MACHINE_MIGRATED':
            case 'SERVER_MIGRATED':
                return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200';
            case 'LICENSE_SUSPENDED':
            case 'LICENSE_REVOKED':
                return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200';
            case 'SETTINGS_UPDATED':
            case 'SYSTEM_LOG_CLEANUP':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700/60 dark:text-gray-300';
        }
    };

    // Filter processing
    const filteredLogs = logs.filter((log: any) => {
        // 1. Search Query filter (case-insensitive across multiple fields)
        if (searchTerm) {
            const query = searchTerm.toLowerCase();
            const licenseKeyMatch = (log.licenseKey || '').toLowerCase().includes(query);
            const customerNameMatch = (log.customerName || '').toLowerCase().includes(query);
            const machineNameMatch = (log.machineName || log.serverName || '').toLowerCase().includes(query);
            const fingerprintMatch = (log.fingerprint || '').toLowerCase().includes(query);
            const ipMatch = (log.ip || log.ipAddress || '').toLowerCase().includes(query);
            const adminEmailMatch = (log.adminEmail || log.email || log.userEmail || '').toLowerCase().includes(query);
            const detailsMatch = renderDetails(log).toLowerCase().includes(query);

            if (!licenseKeyMatch && !customerNameMatch && !machineNameMatch && !fingerprintMatch && !ipMatch && !adminEmailMatch && !detailsMatch) {
                return false;
            }
        }

        // 2. Date Filter
        const logTime = log.createdAt || (log.timestamp?._seconds ? log.timestamp._seconds * 1000 : log.timestamp ? new Date(log.timestamp).getTime() : 0);
        if (dateFilter !== 'all') {
            const now = Date.now();
            if (dateFilter === 'today') {
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                if (logTime < todayStart.getTime()) return false;
            } else if (dateFilter === 'yesterday') {
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
                if (logTime < yesterdayStart.getTime() || logTime >= todayStart.getTime()) return false;
            } else if (dateFilter === '7days') {
                if (logTime < now - 7 * 24 * 60 * 60 * 1000) return false;
            } else if (dateFilter === '30days') {
                if (logTime < now - 30 * 24 * 60 * 60 * 1000) return false;
            } else if (dateFilter === '90days') {
                if (logTime < now - 90 * 24 * 60 * 60 * 1000) return false;
            } else if (dateFilter === 'custom') {
                if (startDate) {
                    const startLimit = new Date(startDate + 'T00:00:00').getTime();
                    if (logTime < startLimit) return false;
                }
                if (endDate) {
                    const endLimit = new Date(endDate + 'T23:59:59').getTime();
                    if (logTime > endLimit) return false;
                }
            }
        }

        // 3. Event Type Filter
        if (eventTypeFilter !== 'all') {
            const type = log.type;
            if (eventTypeFilter === 'license') {
                const licenseTypes = ['LICENSE_CREATED', 'LICENSE_CREATE', 'LICENSE_RENEWED', 'LICENSE_SUSPENDED', 'LICENSE_REVOKED', 'LICENSE_UPDATE'];
                if (!licenseTypes.includes(type)) return false;
            } else if (eventTypeFilter === 'machine') {
                const machineTypes = [
                    'MACHINE_BLOCK', 'SERVER_BLOCK', 
                    'MACHINE_UNBLOCK', 'SERVER_UNBLOCK', 
                    'MACHINE_REACTIVATED', 'SERVER_REACTIVATED', 
                    'MACHINE_REACTIVATED_BY_ADMIN',
                    'MACHINE_SUSPICIOUS', 'SERVER_SUSPICIOUS', 
                    'MACHINE_MIGRATED', 'SERVER_MIGRATED', 
                    'MACHINE_DEACTIVATED', 'DEACTIVATE', 'DEACTIVATED', 
                    'MACHINE_ACTIVATED', 'ACTIVATE', 
                    'HEARTBEAT', 'VALIDATE'
                ];
                if (!machineTypes.includes(type)) return false;
            } else if (eventTypeFilter === 'system') {
                const systemTypes = ['SETTINGS_UPDATED', 'SYSTEM_LOG_CLEANUP'];
                if (!systemTypes.includes(type)) return false;
            }
        }

        // 4. Customer Filter
        if (customerFilter !== 'all') {
            if (log.customerId !== customerFilter) return false;
        }

        // 5. Severity Filter
        if (severityFilter !== 'all') {
            const type = log.type;
            const infoTypes = ['LICENSE_CREATED', 'LICENSE_CREATE', 'LICENSE_RENEWED', 'MACHINE_ACTIVATED', 'ACTIVATE', 'VALIDATE', 'HEARTBEAT', 'SYSTEM_LOG_CLEANUP', 'SETTINGS_UPDATED', 'CUSTOMER_CREATE', 'PRODUCT_CREATE', 'MACHINE_REACTIVATED_BY_ADMIN'];
            const warningTypes = ['MACHINE_SUSPICIOUS', 'SERVER_SUSPICIOUS', 'MACHINE_BLOCK', 'SERVER_BLOCK', 'MACHINE_DEACTIVATED', 'DEACTIVATE', 'DEACTIVATED', 'MACHINE_MIGRATED', 'SERVER_MIGRATED'];
            const criticalTypes = ['LICENSE_REVOKED', 'LICENSE_SUSPENDED'];
            
            if (severityFilter === 'info' && !infoTypes.includes(type)) return false;
            if (severityFilter === 'warning' && !warningTypes.includes(type)) return false;
            if (severityFilter === 'critical' && !criticalTypes.includes(type)) return false;
        }

        return true;
    });

    if (logsLoading) {
        return <div className="text-center py-8 text-gray-500">Loading logs...</div>;
    }

    const itemsPerPage = 25;
    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / itemsPerPage));

    // Slice for current page
    const paginatedLogs = filteredLogs.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePrevPage = () => {
        setCurrentPage(Math.max(currentPage - 1, 1));
    };

    const handleNextPage = () => {
        setCurrentPage(Math.min(currentPage + 1, totalPages));
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Logs & Audit Trail
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    Real-time operational audit trails and system event logs.
                </p>
            </div>

            {/* Filter Panel */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60 p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    {/* Search Bar */}
                    <div className="md:col-span-12">
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Search Logs</label>
                        <input
                            type="text"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                    </div>

                    {/* Date Filter */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Time Period</label>
                        <select
                            value={dateFilter}
                            onChange={(e) => {
                                setDateFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                            <option value="all">All Time</option>
                            <option value="today">Today</option>
                            <option value="yesterday">Yesterday</option>
                            <option value="7days">Last 7 Days</option>
                            <option value="30days">Last 30 Days</option>
                            <option value="90days">Last 90 Days</option>
                            <option value="custom">Custom Range</option>
                        </select>
                    </div>

                    {/* Event Type Filter */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Event Category</label>
                        <select
                            value={eventTypeFilter}
                            onChange={(e) => {
                                setEventTypeFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                            <option value="all">All Events</option>
                            <option value="license">License Events</option>
                            <option value="machine">Machine Events</option>
                            <option value="system">System Events</option>
                        </select>
                    </div>

                    {/* Customer Filter */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Customer</label>
                        <select
                            value={customerFilter}
                            onChange={(e) => {
                                setCustomerFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                            <option value="all">All Customers</option>
                            {customers.map((cust: any) => (
                                <option key={cust.id} value={cust.id}>
                                    {cust.companyName || cust.name || 'Unknown'}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Severity Filter */}
                    <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Severity</label>
                        <select
                            value={severityFilter}
                            onChange={(e) => {
                                setSeverityFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                            <option value="all">All Levels</option>
                            <option value="info">Info</option>
                            <option value="warning">Warning</option>
                            <option value="critical">Critical</option>
                        </select>
                    </div>

                    {/* Custom Range Selectors */}
                    {dateFilter === 'custom' && (
                        <div className="md:col-span-12 grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => {
                                        setEndDate(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-750 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Logs List Section */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Audit Trail
                    </h2>
                    <span className="text-xs text-gray-400 font-medium">
                        Showing {filteredLogs.length} matching events
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead>
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Timestamp
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    License Key
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Details
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {paginatedLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400 italic">
                                        No matching logs found
                                    </td>
                                </tr>
                            ) : (
                                paginatedLogs.map((log: any) => (
                                    <tr key={log.id}>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-mono text-xs">
                                            {formatLogTime(log)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-[10px] uppercase font-bold rounded-md whitespace-nowrap ${getBadgeColorClass(log.type)}`}>
                                                {log.type.replace('SERVER_', 'MACHINE_')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap text-xs">
                                            {log.licenseKey || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                                            <span className="font-medium text-gray-750 dark:text-gray-300">
                                                {renderDetails(log)}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex justify-between items-center px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-750 rounded-b-3xl">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Showing <span className="font-semibold text-gray-800 dark:text-gray-250">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                            <span className="font-semibold text-gray-800 dark:text-gray-250">{Math.min(currentPage * itemsPerPage, filteredLogs.length)}</span> of{' '}
                            <span className="font-semibold text-gray-800 dark:text-gray-250">{filteredLogs.length}</span> logs
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                                className="px-3 py-1 bg-gray-150 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md disabled:opacity-50 text-sm font-medium hover:bg-gray-250 dark:hover:bg-gray-650 transition-colors cursor-pointer"
                            >
                                Prev
                            </button>
                            <button
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 bg-gray-150 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md disabled:opacity-50 text-sm font-medium hover:bg-gray-250 dark:hover:bg-gray-650 transition-colors cursor-pointer"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LogsPage;